/**
 * EditEntryModal Component
 *
 * Modal for editing entries of any type.
 * Pre-populates form with existing values and submits to PATCH endpoint.
 */

import { useState, useEffect, FormEvent } from 'react';
import {
	AnyEntry,
	EntryType,
	WeightEntry,
	MealEntry,
	ExerciseEntry,
	HealthMetricEntry,
	MedicalVisit,
	Prescription,
	ActivityEntry,
} from './EntryCard';
import { PortionStepper } from '../ui/PortionStepper';
import { TimePicker } from '../ui/TimePicker';
import { ImageUploader } from '../ui/ImageUploader';
import { useImages, getDisplayUrl } from '../../hooks/useImages';
import { apiUrl } from '../../lib/api';

interface EditEntryModalProps {
	entry: AnyEntry | null;
	entryType: EntryType | null;
	isOpen: boolean;
	onClose: () => void;
	onSave: () => void;
}

/**
 * Get API endpoint for entry type
 */
function getApiEndpoint(type: EntryType): string {
	switch (type) {
		case 'weight':
			return apiUrl('/api/weight');
		case 'meal':
			return apiUrl('/api/meals');
		case 'exercise':
			return apiUrl('/api/exercise');
		case 'health':
			return apiUrl('/api/health-metrics');
		case 'visit':
			return apiUrl('/api/medical/visits');
		case 'prescription':
			return apiUrl('/api/medical/prescriptions');
		case 'activity':
			return apiUrl('/api/activities');
	}
}

/**
 * Format time from Date to HH:mm string
 */
function formatTimeString(date: Date | null | undefined): string {
	if (!date) return '';
	const d = new Date(date);
	return d.toTimeString().slice(0, 5);
}

export function EditEntryModal({
	entry,
	entryType,
	isOpen,
	onClose,
	onSave,
}: EditEntryModalProps) {
	// Fetch images for the entry
	const { images, refetch: refetchImages } = useImages(
		entryType || '',
		entry?.id || ''
	);

	// Common state
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Weight form state
	const [weight, setWeight] = useState('');
	const [weightNotes, setWeightNotes] = useState('');

	// Meal form state
	const [mealType, setMealType] = useState('breakfast');
	const [mealDescription, setMealDescription] = useState('');
	const [carbPortions, setCarbPortions] = useState(1);
	const [proteinPortions, setProteinPortions] = useState(1);
	const [mealStartTime, setMealStartTime] = useState('');
	const [mealEndTime, setMealEndTime] = useState('');
	const [mealNotes, setMealNotes] = useState('');

	// Exercise form state
	const [sessionType, setSessionType] = useState('morning');
	const [exerciseType, setExerciseType] = useState('');
	const [duration, setDuration] = useState('');
	const [exerciseNotes, setExerciseNotes] = useState('');

	// Health form state
	const [metricType, setMetricType] = useState('medication');
	const [healthDescription, setHealthDescription] = useState('');
	const [hasBowelMovement, setHasBowelMovement] = useState(true);
	const [healthTime, setHealthTime] = useState('');
	const [healthNotes, setHealthNotes] = useState('');

	// Visit form state
	const [doctorName, setDoctorName] = useState('');
	const [specialty, setSpecialty] = useState('');
	const [hospitalClinic, setHospitalClinic] = useState('');
	const [diagnosis, setDiagnosis] = useState('');
	const [visitNotes, setVisitNotes] = useState('');

	// Prescription form state
	const [medicationName, setMedicationName] = useState('');
	const [dosage, setDosage] = useState('');
	const [frequency, setFrequency] = useState('');
	const [prescriptionDuration, setPrescriptionDuration] = useState('');
	const [prescriptionNotes, setPrescriptionNotes] = useState('');

	// Activity form state
	const [activityName, setActivityName] = useState('');
	const [activityDuration, setActivityDuration] = useState('');
	const [activityNotes, setActivityNotes] = useState('');

	// Initialize form values when entry changes
	useEffect(() => {
		if (!entry || !entryType) return;

		setError(null);

		switch (entryType) {
			case 'weight': {
				const e = entry as WeightEntry;
				setWeight(e.weight);
				setWeightNotes(e.notes || '');
				break;
			}
			case 'meal': {
				const e = entry as MealEntry;
				setMealType(e.mealType);
				setMealDescription(e.description);
				setCarbPortions(parseFloat(e.carbPortions || '1') || 1);
				setProteinPortions(parseFloat(e.proteinPortions || '1') || 1);
				setMealStartTime(formatTimeString(e.startTime));
				setMealEndTime(formatTimeString(e.endTime));
				setMealNotes(e.notes || '');
				break;
			}
			case 'exercise': {
				const e = entry as ExerciseEntry;
				setSessionType(e.sessionType);
				setExerciseType(e.exerciseType);
				setDuration(e.durationMinutes);
				setExerciseNotes(e.notes || '');
				break;
			}
			case 'health': {
				const e = entry as HealthMetricEntry;
				setMetricType(e.metricType);
				setHealthDescription(e.description || '');
				setHasBowelMovement(e.hasBowelMovement === 'yes');
				setHealthTime(formatTimeString(e.time));
				setHealthNotes(e.notes || '');
				break;
			}
			case 'visit': {
				const e = entry as MedicalVisit;
				setDoctorName(e.doctorName);
				setSpecialty(e.specialty || '');
				setHospitalClinic(e.hospitalClinic || '');
				setDiagnosis(e.diagnosis || '');
				setVisitNotes(e.notes || '');
				break;
			}
			case 'prescription': {
				const e = entry as Prescription;
				setMedicationName(e.medicationName);
				setDosage(e.dosage || '');
				setFrequency(e.frequency || '');
				setPrescriptionDuration(e.duration || '');
				setPrescriptionNotes(e.notes || '');
				break;
			}
			case 'activity': {
				const e = entry as ActivityEntry;
				setActivityName(e.activityName);
				setActivityDuration(e.durationMinutes);
				setActivityNotes(e.notes || '');
				break;
			}
		}
	}, [entry, entryType]);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!entry || !entryType) return;

		setIsSubmitting(true);
		setError(null);

		try {
			let body: Record<string, unknown> = {};

			switch (entryType) {
				case 'weight':
					if (!weight || parseFloat(weight) <= 0) {
						throw new Error('Berat harus berupa angka positif');
					}
					body = {
						weight,
						notes: weightNotes || undefined,
					};
					break;
				case 'meal':
					if (!mealDescription.trim()) {
						throw new Error('Deskripsi makanan harus diisi');
					}
					if (mealStartTime && mealEndTime && mealStartTime >= mealEndTime) {
						throw new Error('Waktu selesai harus setelah waktu mulai');
					}
					body = {
						mealType,
						description: mealDescription.trim(),
						carbPortion: carbPortions.toString(),
						proteinPortion: proteinPortions.toString(),
						startTime: mealStartTime || undefined,
						endTime: mealEndTime || undefined,
						notes: mealNotes || undefined,
					};
					break;
				case 'exercise':
					if (!exerciseType.trim()) {
						throw new Error('Jenis olahraga harus diisi');
					}
					if (!duration || parseInt(duration, 10) <= 0) {
						throw new Error('Durasi harus berupa angka positif');
					}
					body = {
						sessionType,
						exerciseType: exerciseType.trim(),
						durationMinutes: duration,
						notes: exerciseNotes || undefined,
					};
					break;
				case 'health': {
					const isBowel = metricType === 'bowel' || metricType === 'bowel_movement';
					if (!isBowel && !healthDescription.trim()) {
						throw new Error('Deskripsi harus diisi');
					}
					body = {
						metricType,
					};
					if (isBowel) {
						body.hasBowelMovement = hasBowelMovement;
						if (healthTime) body.time = healthTime;
					} else {
						body.description = healthDescription.trim();
					}
					if (healthNotes) body.notes = healthNotes;
					break;
				}
				case 'visit':
					if (!doctorName.trim()) {
						throw new Error('Nama dokter harus diisi');
					}
					body = {
						doctorName: doctorName.trim(),
						specialty: specialty || undefined,
						hospitalClinic: hospitalClinic || undefined,
						diagnosis: diagnosis || undefined,
						notes: visitNotes || undefined,
					};
					break;
				case 'prescription':
					if (!medicationName.trim()) {
						throw new Error('Nama obat harus diisi');
					}
					body = {
						medicationName: medicationName.trim(),
						dosage: dosage || undefined,
						frequency: frequency || undefined,
						duration: prescriptionDuration || undefined,
						notes: prescriptionNotes || undefined,
					};
					break;
				case 'activity':
					if (!activityName.trim()) {
						throw new Error('Nama aktivitas harus diisi');
					}
					if (!activityDuration || parseInt(activityDuration, 10) <= 0) {
						throw new Error('Durasi harus berupa angka positif');
					}
					body = {
						activityName: activityName.trim(),
						durationMinutes: activityDuration,
						notes: activityNotes || undefined,
					};
					break;
			}

			const endpoint = getApiEndpoint(entryType);
			const res = await fetch(`${endpoint}/${entry.id}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify(body),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan perubahan');
			}

			onSave();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen || !entry || !entryType) return null;

	const renderForm = () => {
		switch (entryType) {
			case 'weight':
				return (
					<>
						<div>
							<label
								htmlFor="edit-weight"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Berat (kg) <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								inputMode="decimal"
								id="edit-weight"
								value={weight}
								onChange={(e) => setWeight(e.target.value)}
								placeholder="Contoh: 75.55"
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-weight-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-weight-notes"
								value={weightNotes}
								onChange={(e) => setWeightNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);

			case 'meal':
				return (
					<>
						<div className="flex flex-wrap gap-2">
							{[
								{ id: 'breakfast', label: 'Sarapan' },
								{ id: 'snack1', label: 'Snack 1' },
								{ id: 'lunch', label: 'Makan Siang' },
								{ id: 'snack2', label: 'Snack 2' },
								{ id: 'dinner', label: 'Makan Malam' },
							].map((tab) => (
								<button
									key={tab.id}
									type="button"
									onClick={() => setMealType(tab.id)}
									className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
										mealType === tab.id
											? 'bg-blue-100 text-blue-700'
											: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
						<div>
							<label
								htmlFor="edit-meal-description"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Deskripsi <span className="text-red-500">*</span>
							</label>
							<textarea
								id="edit-meal-description"
								value={mealDescription}
								onChange={(e) => setMealDescription(e.target.value)}
								rows={2}
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
						<div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
							<PortionStepper
								value={carbPortions}
								onChange={setCarbPortions}
								label="Karbohidrat"
								disabled={isSubmitting}
							/>
							<PortionStepper
								value={proteinPortions}
								onChange={setProteinPortions}
								label="Protein"
								disabled={isSubmitting}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<TimePicker
								value={mealStartTime}
								onChange={setMealStartTime}
								label="Waktu Mulai"
								id="edit-meal-start"
								disabled={isSubmitting}
							/>
							<TimePicker
								value={mealEndTime}
								onChange={setMealEndTime}
								label="Waktu Selesai"
								id="edit-meal-end"
								disabled={isSubmitting}
							/>
						</div>
						<div>
							<label
								htmlFor="edit-meal-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-meal-notes"
								value={mealNotes}
								onChange={(e) => setMealNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);

			case 'exercise':
				return (
					<>
						<div className="flex gap-2">
							{[
								{ id: 'morning', label: 'Pagi' },
								{ id: 'afternoon', label: 'Sore' },
							].map((tab) => (
								<button
									key={tab.id}
									type="button"
									onClick={() => setSessionType(tab.id)}
									className={`px-4 py-1.5 text-sm font-medium rounded-full transition ${
										sessionType === tab.id
											? 'bg-blue-100 text-blue-700'
											: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
						<div>
							<label
								htmlFor="edit-exercise-type"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Jenis Olahraga <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="edit-exercise-type"
								value={exerciseType}
								onChange={(e) => setExerciseType(e.target.value)}
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-exercise-duration"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Durasi (menit) <span className="text-red-500">*</span>
							</label>
							<input
								type="number"
								id="edit-exercise-duration"
								value={duration}
								onChange={(e) => setDuration(e.target.value)}
								min="1"
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-exercise-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-exercise-notes"
								value={exerciseNotes}
								onChange={(e) => setExerciseNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);

			case 'health': {
				const isBowel = metricType === 'bowel' || metricType === 'bowel_movement';
				return (
					<>
						<div className="flex flex-wrap gap-2">
							{[
								{ id: 'medication', label: 'Obat' },
								{ id: 'vitamin', label: 'Vitamin' },
								{ id: 'bowel_movement', label: 'BAB' },
								{ id: 'symptom', label: 'Gejala' },
							].map((tab) => (
								<button
									key={tab.id}
									type="button"
									onClick={() => setMetricType(tab.id)}
									className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
										metricType === tab.id
											? 'bg-blue-100 text-blue-700'
											: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
						{isBowel ? (
							<>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Apakah BAB hari ini?
									</label>
									<div className="flex gap-3">
										<button
											type="button"
											onClick={() => setHasBowelMovement(true)}
											className={`flex-1 py-3 px-4 rounded-lg font-medium transition ${
												hasBowelMovement
													? 'bg-green-100 text-green-700 border-2 border-green-500'
													: 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
											}`}
										>
											Ya
										</button>
										<button
											type="button"
											onClick={() => setHasBowelMovement(false)}
											className={`flex-1 py-3 px-4 rounded-lg font-medium transition ${
												!hasBowelMovement
													? 'bg-red-100 text-red-700 border-2 border-red-500'
													: 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
											}`}
										>
											Tidak
										</button>
									</div>
								</div>
								{hasBowelMovement && (
									<TimePicker
										value={healthTime}
										onChange={setHealthTime}
										label="Waktu (opsional)"
										id="edit-health-time"
										disabled={isSubmitting}
									/>
								)}
							</>
						) : (
							<div>
								<label
									htmlFor="edit-health-description"
									className="block text-sm font-medium text-gray-700 mb-1"
								>
									Deskripsi <span className="text-red-500">*</span>
								</label>
								<textarea
									id="edit-health-description"
									value={healthDescription}
									onChange={(e) => setHealthDescription(e.target.value)}
									rows={2}
									required
									disabled={isSubmitting}
									className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
								/>
							</div>
						)}
						<div>
							<label
								htmlFor="edit-health-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-health-notes"
								value={healthNotes}
								onChange={(e) => setHealthNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);
			}

			case 'visit':
				return (
					<>
						<div>
							<label
								htmlFor="edit-doctor-name"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Nama Dokter <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="edit-doctor-name"
								value={doctorName}
								onChange={(e) => setDoctorName(e.target.value)}
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-specialty"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Spesialisasi (opsional)
							</label>
							<input
								type="text"
								id="edit-specialty"
								value={specialty}
								onChange={(e) => setSpecialty(e.target.value)}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-hospital"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Rumah Sakit/Klinik (opsional)
							</label>
							<input
								type="text"
								id="edit-hospital"
								value={hospitalClinic}
								onChange={(e) => setHospitalClinic(e.target.value)}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-diagnosis"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Diagnosis (opsional)
							</label>
							<textarea
								id="edit-diagnosis"
								value={diagnosis}
								onChange={(e) => setDiagnosis(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-visit-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-visit-notes"
								value={visitNotes}
								onChange={(e) => setVisitNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);

			case 'prescription':
				return (
					<>
						<div>
							<label
								htmlFor="edit-medication-name"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Nama Obat <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="edit-medication-name"
								value={medicationName}
								onChange={(e) => setMedicationName(e.target.value)}
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-dosage"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Dosis (opsional)
							</label>
							<input
								type="text"
								id="edit-dosage"
								value={dosage}
								onChange={(e) => setDosage(e.target.value)}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-frequency"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Frekuensi (opsional)
							</label>
							<input
								type="text"
								id="edit-frequency"
								value={frequency}
								onChange={(e) => setFrequency(e.target.value)}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-duration"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Durasi (opsional)
							</label>
							<input
								type="text"
								id="edit-duration"
								value={prescriptionDuration}
								onChange={(e) => setPrescriptionDuration(e.target.value)}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-prescription-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-prescription-notes"
								value={prescriptionNotes}
								onChange={(e) => setPrescriptionNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);

			case 'activity':
				return (
					<>
						<div>
							<label
								htmlFor="edit-activity-name"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Nama Aktivitas <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="edit-activity-name"
								value={activityName}
								onChange={(e) => setActivityName(e.target.value)}
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-activity-duration"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Durasi (menit) <span className="text-red-500">*</span>
							</label>
							<input
								type="number"
								id="edit-activity-duration"
								value={activityDuration}
								onChange={(e) => setActivityDuration(e.target.value)}
								min="1"
								required
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100"
							/>
						</div>
						<div>
							<label
								htmlFor="edit-activity-notes"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Catatan (opsional)
							</label>
							<textarea
								id="edit-activity-notes"
								value={activityNotes}
								onChange={(e) => setActivityNotes(e.target.value)}
								rows={2}
								disabled={isSubmitting}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100"
							/>
						</div>
					</>
				);
		}
	};

	const getTitle = () => {
		const titles: Record<EntryType, string> = {
			weight: 'Edit Berat Badan',
			meal: 'Edit Makanan',
			exercise: 'Edit Olahraga',
			health: 'Edit Kesehatan',
			visit: 'Edit Kunjungan Dokter',
			prescription: 'Edit Resep',
			activity: 'Edit Aktivitas',
		};
		return titles[entryType];
	};

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
				onClick={onClose}
			/>

			{/* Modal */}
			<div className="flex min-h-full items-center justify-center p-4">
				<div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
					{/* Header */}
					<div className="flex items-center justify-between p-4 border-b">
						<h2 className="text-lg font-semibold text-gray-900">{getTitle()}</h2>
						<button
							onClick={onClose}
							className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit} className="p-4 space-y-4">
						{/* Error Display */}
						{error && (
							<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
								{error}
							</div>
						)}

						{renderForm()}

						{/* Images Section */}
						{entry && entryType && (
							<div className="mt-4 pt-4 border-t border-gray-200">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Foto
								</label>
								<ImageUploader
									entryType={entryType}
									entryId={entry.id}
									existingImages={images.map((img) => ({
										id: img.id,
										url: getDisplayUrl(img),
									}))}
									onImagesChange={() => refetchImages()}
								/>
							</div>
						)}

						{/* Actions - Sticky on mobile for easy access */}
						<div className="sticky bottom-0 flex gap-3 pt-4 -mx-4 px-4 pb-1 bg-white border-t sm:static sm:mx-0 sm:px-0 sm:pb-0">
							<button
								type="button"
								onClick={onClose}
								disabled={isSubmitting}
								className="flex-1 px-4 py-3 min-h-12 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium disabled:opacity-50 sm:py-2 sm:min-h-0"
							>
								Batal
							</button>
							<button
								type="submit"
								disabled={isSubmitting}
								className="flex-1 px-4 py-3 min-h-12 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 shadow-lg sm:py-2 sm:min-h-0 sm:shadow-none"
							>
								{isSubmitting ? 'Menyimpan...' : 'Simpan'}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
